import { useCallback, useEffect, useMemo, useState } from "react"
import { replyTargets } from "@/nostr/api/feed"
import {
  buildReplyTree,
  collectAncestors,
  descendantsOf,
  threadParentId,
} from "@/nostr/api/thread"
import { KINDS, type NostrEvent, type NostrFilter } from "@/nostr/types"
import { useEventVersion, useNostr } from "@/state/NostrContext"
import { useRelays } from "@/state/RelayContext"
import { useEventById, useEventsByIds } from "./useProfiles"

export interface ThreadResult {
  note?: NostrEvent
  /** Ancestors above the focused note, root first. */
  ancestors: NostrEvent[]
  root?: NostrEvent
  /** Every reply in the focused note's subtree. */
  replies: NostrEvent[]
  /** parent id -> direct replies (for nested rendering). */
  children: Map<string, NostrEvent[]>
  /** Replies whose parent could not be loaded (shown with an explicit label). */
  orphans: NostrEvent[]
  loading: boolean
  /** Re-reads the thread (used after publishing a reply). */
  refresh: () => void
}

/** Extra query rounds used to complete deep threads. */
const MAX_THREAD_ROUNDS = 2
const MAX_IDS_PER_ROUND = 60

/**
 * Loads a note, its ancestor chain and its reply subtree.
 *
 * Threads are incomplete if only `#e` on the root is queried: many clients write
 * a reply that tags *only* its direct parent. The hook therefore runs a few
 * bounded rounds:
 *
 *  1. `#e` on the focused note and the thread root (direct replies);
 *  2. `#e` on every reply discovered so far (children that do not tag the root);
 *  3. by-id requests for parents that were referenced but never returned.
 *
 * Each round is one REQ per relay and is closed after EOSE, so a deep thread
 * costs a handful of queries instead of one long-lived subscription per level.
 */
export function useThread(noteId?: string): ThreadResult {
  const { pool, store } = useNostr()
  const { appRelays } = useRelays()
  const version = useEventVersion(store)
  const note = useEventById(noteId)
  const [refreshToken, setRefreshToken] = useState(0)

  const { ancestors, missingId } = useMemo(
    () => collectAncestors(note, (id) => store.get(id)),
    // the store version makes this recompute as ancestors arrive
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [note, version, store],
  )
  useEventsByIds(missingId ? [missingId] : [])

  const rootId = note ? replyTargets(note).rootId : undefined
  const parentIdsKey = useMemo(
    () => [...new Set([noteId, rootId].filter((id): id is string => Boolean(id)))].join(","),
    [noteId, rootId],
  )

  const [replies, setReplies] = useState<NostrEvent[]>([])
  const [loading, setLoading] = useState(Boolean(noteId))

  const refresh = useCallback(() => setRefreshToken((value) => value + 1), [])

  useEffect(() => {
    setReplies([])
    if (!noteId) {
      setLoading(false)
      return
    }
    const focusedId = noteId
    const seedParents = parentIdsKey.split(",").filter(Boolean)
    const relays = appRelays

    let cancelled = false
    const subscriptions: Array<{ close: () => void }> = []
    const collected = new Map<string, NostrEvent>()
    const queriedParents = new Set<string>()
    let round = 0

    const publish = (isLoading: boolean) => {
      if (cancelled) return
      setReplies(descendantsOf(focusedId, [...collected.values()]))
      setLoading(isLoading)
    }

    const runRound = (parentIds: string[], idsToFetch: string[]) => {
      const filters: NostrFilter[] = []
      if (parentIds.length > 0) {
        filters.push({ kinds: [KINDS.NOTE], "#e": parentIds, limit: 500 })
        for (const id of parentIds) queriedParents.add(id)
      }
      if (idsToFetch.length > 0) filters.push({ ids: idsToFetch })
      if (filters.length === 0) return

      const subscription = pool.subscribe(filters, {
        relays,
        onEvent: (event) => {
          if (event.id === focusedId) return
          store.add(event)
          if (!collected.has(event.id)) collected.set(event.id, event)
        },
        onEose: () => {
          // Lookups are query-shaped: close instead of keeping a subscription
          // open per thread level (relays cap concurrent REQs).
          subscription.close()
          if (cancelled) return
          publish(false)
          nextRound()
        },
      })
      subscriptions.push(subscription)
    }

    const nextRound = () => {
      if (cancelled || round >= MAX_THREAD_ROUNDS) return
      round += 1

      const known = new Set([focusedId, ...queriedParents, ...collected.keys()])
      const missingParents = [
        ...new Set(
          [...collected.values()]
            .map((event) => threadParentId(event))
            .filter((id): id is string => typeof id === "string" && !known.has(id)),
        ),
      ].slice(0, MAX_IDS_PER_ROUND)

      // Children of replies we already have, whose `#e` tags we never queried.
      const unqueriedParents = descendantsOf(focusedId, [...collected.values()])
        .map((event) => event.id)
        .filter((id) => !queriedParents.has(id))
        .slice(0, MAX_IDS_PER_ROUND)

      if (missingParents.length === 0 && unqueriedParents.length === 0) return
      runRound(unqueriedParents, missingParents)
    }

    setLoading(true)
    runRound(seedParents, [])

    return () => {
      cancelled = true
      for (const subscription of subscriptions) subscription.close()
    }
  }, [noteId, parentIdsKey, pool, store, appRelays, refreshToken])

  const tree = useMemo(
    () => buildReplyTree(replies, noteId ? [noteId] : []),
    [replies, noteId],
  )

  return {
    note,
    ancestors,
    root: ancestors[0],
    replies,
    children: tree.children,
    orphans: tree.orphans,
    loading,
    refresh,
  }
}
